import { DeleteOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Radio,
  Row,
  Space,
  Typography,
  type DatePickerProps,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { DESTINACAO_OPTIONS } from "../utils";

const { RangePicker } = DatePicker;

const disabledPeriodoDate: DatePickerProps["disabledDate"] = (
  current,
  { from, type }
) => {
  if (current.isBefore(dayjs(), "day")) return true;
  if (!from || type !== "date") return false;

  return (
    current.isBefore(from.add(1, "day"), "day") ||
    current.diff(from, "day") > 7
  );
};

interface Props {
  index: number;
  fieldName: number;
  namePathPrefix?: Array<string | number>;
  solicitarDatas: boolean;
  solicitarDestinacao: boolean;
  showRemove: boolean;
  onRemove: () => void;
  receberOportunidadesInicial?: boolean;
  editavel?: boolean;
  periodoLabel?: string;
}

export function PeriodoForm({
  index,
  fieldName,
  namePathPrefix = [],
  solicitarDatas,
  solicitarDestinacao,
  showRemove,
  onRemove,
  receberOportunidadesInicial,
  editavel,
  periodoLabel,
}: Props) {
  const form = Form.useFormInstance();
  const periodoPath = ["periodos", fieldName];
  const [receberOportunidadesAtual, setReceberOportunidadesAtual] = useState<
    boolean | undefined
  >(receberOportunidadesInicial);
  const receberOportunidadesDoFormulario = Form.useWatch(
    [...periodoPath, "receberOportunidades"],
    form
  );
  const receberOportunidadesExibido =
    receberOportunidadesAtual ?? receberOportunidadesDoFormulario;
  useEffect(() => {
    if (typeof receberOportunidadesDoFormulario === "boolean") {
      setReceberOportunidadesAtual(receberOportunidadesDoFormulario);
    }
  }, [receberOportunidadesDoFormulario]);
  const checkIn = Form.useWatch(
    [...periodoPath, "checkIn"],
    form
  ) as Dayjs | undefined;
  const checkOut = Form.useWatch(
    [...periodoPath, "checkOut"],
    form
  ) as Dayjs | undefined;
  const intervaloSelecionado =
    checkIn && checkOut ? ([checkIn, checkOut] as [Dayjs, Dayjs]) : undefined;
  return (
    <Card
      size="small"
      style={{ borderRadius: 18, borderColor: "#d9e4e8", marginBottom: 16 }}
    >
      <Space direction="vertical" size={16} style={{ width: "100%" }}>
        <Row justify="space-between" align="middle">
          <Col>
            {editavel === false && periodoLabel ? (
              <Typography.Title level={5} style={{ margin: 0 }}>
                {periodoLabel}
              </Typography.Title>
            ) : null}
          </Col>
          <Col>
            {editavel !== false && showRemove ? (
              <Button type="text" icon={<DeleteOutlined />} onClick={onRemove}>
                Remover
              </Button>
            ) : null}
          </Col>
        </Row>

        {editavel !== false && solicitarDatas ? (
          <Row gutter={16}>
            <Col xs={24}>
              <Form.Item
                name={[...namePathPrefix, fieldName, "checkIn"]}
                hidden
              >
                <span aria-hidden="true" />
              </Form.Item>
              <Form.Item
                name={[...namePathPrefix, fieldName, "checkOut"]}
                hidden
              >
                <span aria-hidden="true" />
              </Form.Item>
              <Form.Item
                label="Check-in e check-out"
                required
              >
                <RangePicker
                  value={intervaloSelecionado}
                  format="DD/MM/YYYY"
                  placeholder={["Check-in", "Check-out"]}
                  style={{ width: "100%" }}
                  disabledDate={disabledPeriodoDate}
                  onChange={(datas) => {
                    form.setFieldValue(
                      [...periodoPath, "checkIn"],
                      datas?.[0]
                    );
                    form.setFieldValue(
                      [...periodoPath, "checkOut"],
                      datas?.[1]
                    );
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
        ) : null}

        {solicitarDestinacao ? (
          <Form.Item
            label="Gostaria de receber oportunidades de aluguel ou troca para este período?"
            name={[...namePathPrefix, fieldName, "receberOportunidades"]}
            rules={[{ required: true, message: "Selecione uma opção." }]}
          >
            <Radio.Group
              options={[{ label: "Sim", value: true }, { label: "Não", value: false }]}
              onChange={(event) => {
                const receber = event.target.value as boolean;
                setReceberOportunidadesAtual(receber);
                form.setFieldValue(
                  [...periodoPath, "destinacao"],
                  receber ? "OPORTUNIDADES_MULTICOTAS" : undefined
                );
              }}
            />
          </Form.Item>
        ) : null}

        {solicitarDestinacao && receberOportunidadesExibido === false ? (
          <Form.Item
            label="O que você pretende fazer com este período?"
            name={[...namePathPrefix, fieldName, "destinacao"]}
            rules={[{ required: true, message: "Selecione a destinação." }]}
          >
            <Radio.Group
              options={DESTINACAO_OPTIONS}
              optionType="default"
              style={{ display: "grid", gap: 8 }}
            />
          </Form.Item>
        ) : null}

      </Space>
    </Card>
  );
}
